import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "uxp-toolchain"
    }}>{`UXP Toolchain`}</h1>
    <p>{`UXP plugins are built using modern JavaScript with the UI created using subsets of HTML and CSS. See `}<a parentName="p" {...{
        "href": "../../#the-uxp-technology-stack"
      }}>{`Technology Stack`}</a>{` for more detail.`}</p>
    <h2 {...{
      "id": "tools-for-development"
    }}>{`Tools for Development`}</h2>
    <h3 {...{
      "id": "a-basic-toolchain"
    }}>{`A Basic Toolchain`}</h3>
    <p>{`In order to build a UXP-based plugin, you'll need the following pieces of software:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`A good text editor. Any modern developer-friendly text editor should work, but many people are using `}<a parentName="p" {...{
            "href": "https://code.visualstudio.com"
          }}>{`Visual Studio Code`}</a>{`, which has deep integrations with the other pieces needed for UXP, and a wide variety of its own plugins to help with code formatting, syntax checking, JSON parsing, and so on.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<a parentName="p" {...{
            "href": "../../devtool/"
          }}>{`UXP Developer Tool`}</a>{` provides a GUI that allows you to load, unload, and debug plugins. It also creates the `}<inlineCode parentName="p">{`.ccx`}</inlineCode>{` files you'll need to distribute your plugin to others.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`If you're using the UXP CLI, you'll need a command-line environment. The Terminal app in macOS works fine, as does the Windows Command Prompt. VS Code has a built-in terminal window which is convenient. And there are a dozen other command-line interpreters on both platforms, including Windows PowerShell. If you're building plugins that don't involve using React, you probably don't need to worry about this.`}</p>
      </li>
    </ul>
    <p><a parentName="p" {...{
        "href": "../../devtool/udt-walkthrough/"
      }}>{`Here is a step-by-step walkthrough showing the creation of a Photoshop UXP plugin`}</a>{`.`}</p>
    <h3 {...{
      "id": "advanced-toolchain"
    }}>{`Advanced Toolchain`}</h3>
    <p>{` If you're using the UXP Developer Tool, and using plain JavaScript instead of React, you probably don't need any of the following tools.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://nodejs.org/en/"
          }}>{`Node.js`}</a>{`: a JavaScript runtime environment. It's often used as a backend server in web environments. In the case of UXP, it's used as an engine to help manage installing plugins while using a command-line environment.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://www.npmjs.com"
          }}>{`npm`}</a>{`: a "package manager" bundled with Node which helps manage your project's dependencies (i.e., other code and files that are needed to make your plugin work). If you're just using bare HTML/CSS/js, you probably don't need npm.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://yarnpkg.com"
          }}>{`yarn`}</a>{`: a "better" package manager than npm for Node. Many people, including those developing Photoshop UXP tools, are using yarn instead of npm.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "installing-the-tooling"
    }}>{`Installing the Tooling`}</h3>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`UXP Developer Tool for macOS and Windows`}</strong>{` - See the instructions `}<a parentName="p" {...{
            "href": "/photoshop/uxp/devtool/"
          }}>{`here`}</a>{` to install and run the UXP Developer Toolkit.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Node.js`}</strong>{` - Go to the `}<a parentName="p" {...{
            "href": "https://nodejs.org/en/download/"
          }}>{`Node.js download page`}</a>{`, download the installer for your platform, and run it. This will also install npm. `}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`yarn`}</strong>{`: In order to install yarn you'll need to have npm installed first (see above). After that, use this command:`}</p>
        <pre parentName="li"><code parentName="pre" {...{
            "className": "language-bash"
          }}>{`npm install yarn --global
`}</code></pre>
      </li>
    </ul>
    <h2 {...{
      "id": "intellectual-toolchain"
    }}>{`Intellectual Toolchain`}</h2>
    <p>{`Before starting to build a plugin, you should have a good working knowledge of modern JavaScript. If you're coming from an ExtendScript background and haven't used recent JavaScript, you'll want to make sure that you understand concepts including asynchronous functions, newer JavaScript syntax (e.g., let/const vs "var"), and things like anonymous functions and arrow function expressions. There are thousands of websites and online videos that explain these concepts.`}</p>
    <p>{`If you're going to be building a non-trivial UI into your plugin, you should also become familiar with `}<a parentName="p" {...{
        "href": "https://reactjs.org"
      }}>{`React`}</a>{`.`}</p>
    <p>{`In order to build React-based plugins, you'll need to know a bit about `}<a parentName="p" {...{
        "href": "https://nodejs.org/en/"
      }}>{`NodeJS`}</a>{` and `}<a parentName="p" {...{
        "href": "https://www.npmjs.com"
      }}>{`npm, the Node Package Manager`}</a>{`. Some plugin examples use `}<a parentName="p" {...{
        "href": "https://yarnpkg.com"
      }}>{`Yarn`}</a>{`, an alternative to npm.`}</p>
    <h2 {...{
      "id": "useful-utilities-and-resources"
    }}>{`Useful Utilities and Resources`}</h2>
    <h3 {...{
      "id": "kitchen-sink-example"
    }}>{`Kitchen Sink Example`}</h3>
    <p>{`This is sample code, not actually a tool. But the `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/ui-kitchen-sink"
      }}>{`uxp-ui-kitchen-sink-plugin`}</a>{` is very useful in showing the kinds of controls you can use in UXP. It covers both Spectrum and native controls.`}</p>
    <h3 {...{
      "id": "html-playground"
    }}>{`HTML Playground`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/AdobeDocs/uxp-photoshop-plugin-samples/tree/main/ui-playground"
      }}>{`HTML Playground`}</a>{` plugin is designed to make it easy to test out simple HTML layouts in Photoshop using UXP's layout engine. To use this, install the plugin using the UXP Developer Tool.`}</p>
    <h3 {...{
      "id": "batchplay-logger-for-extendscript-developers"
    }}>{`batchPlay Logger for ExtendScript developers`}</h3>
    <p>{`Plug the `}<a parentName="p" {...{
        "href": "https://github.com/adobe-uxp/ps-es-to-uxp"
      }}>{`ExtendScript BatchPlay logger`}</a>{` jsx code into your ExtendScript project, and it will print out all your `}<inlineCode parentName="p">{`executeAction`}</inlineCode>{` and `}<inlineCode parentName="p">{`executeActionGet`}</inlineCode>{` methods in a format suitable for use with UXP's BatchPlay.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      